import axios from 'axios';
import { ApiResponse, Transaction } from '../../types/api';

export interface TransactionFilters {
  search?: string;
  account_id?: number;
  blotter_id?: number;
  security?: string;
  start_date?: string;
  end_date?: string;
  min_commission?: number;
  max_commission?: number;
  has_override?: boolean;
  is_complete?: boolean;
  page?: number;
  per_page?: number;
}

export interface TransactionSummary {
  total_commission: number;
  average_commission_rate: number;
  override_count: number;
}

export interface TransactionListResponse {
  data: Transaction[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  summary: TransactionSummary;
}

class TransactionService {
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Get all transactions with filters and pagination
   */
  async getTransactions(filters?: TransactionFilters): Promise<TransactionListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.baseURL}/transactions?${queryString}` : `${this.baseURL}/transactions`;

    const response = await axios.get<TransactionListResponse>(url, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  /**
   * Get single transaction by ID
   */
  async getTransaction(id: number): Promise<ApiResponse<Transaction>> {
    const response = await axios.get<ApiResponse<Transaction>>(
      `${this.baseURL}/transactions/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Update transaction
   */
  async updateTransaction(
    id: number,
    data: Partial<Pick<Transaction, 'is_complete' | 'account_string' | 'security'>>
  ): Promise<ApiResponse<Transaction>> {
    const response = await axios.put<ApiResponse<Transaction>>(
      `${this.baseURL}/transactions/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Export transactions (placeholder for future implementation)
   */
  async exportTransactions(filters?: TransactionFilters, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    params.append('format', format);

    const response = await axios.get(
      `${this.baseURL}/transactions/export?${params.toString()}`,
      {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      }
    );

    return response.data;
  }
}

export default new TransactionService();
